import { useEffect, useState, useMemo } from "react";
import { useNavigate, Link } from "react-router-dom";
import { Header } from "@/components/layout/Header";
import { Footer } from "@/components/layout/Footer";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { useAuth } from "@/lib/auth";
import { useTrips } from "@/hooks/useTrips";
import { CreateTripDialog } from "@/components/trips/CreateTripDialog";
import { ImportEmailDialog } from "@/components/trips/ImportEmailDialog";
import { TripActionsMenu } from "@/components/trips/TripActionsMenu";
import { safeFormat } from "@/lib/formatDateTime";

// Native date parsing helpers to avoid date-fns locale issues
function safeParseDateString(dateStr: string | undefined | null): Date | null {
  if (!dateStr || typeof dateStr !== 'string') return null;
  try {
    const date = new Date(dateStr);
    if (isNaN(date.getTime())) return null;
    return date;
  } catch {
    return null;
  }
}

function isPastDate(date: Date): boolean {
  return date.getTime() < Date.now();
}
import { MapPin, Calendar, Plane, Hotel, Mail, Plus, Search, ChevronRight, CheckCircle } from "lucide-react";
import { Card } from "@/components/ui/card";

export default function Trips() {
  const navigate = useNavigate();
  const { user, loading: authLoading } = useAuth();
  const { data: trips, isLoading: tripsLoading } = useTrips();
  const [searchQuery, setSearchQuery] = useState("");

  useEffect(() => {
    if (!authLoading && !user) {
      navigate("/auth");
    }
  }, [user, authLoading, navigate]);

  // Determine if a trip is completed (end date is in the past)
  const isTripCompleted = (endDate: string | null) => {
    if (!endDate) return false;
    try {
      const date = safeParseDateString(endDate);
      return date !== null && isPastDate(date);
    } catch {
      return false;
    }
  };

  // Filter trips based on search query
  const filteredTrips = useMemo(() => {
    if (!trips) return [];
    if (!searchQuery.trim()) return trips;

    const query = searchQuery.toLowerCase();
    return trips.filter((trip) => {
      // Search in display name
      if (trip.display_name.toLowerCase().includes(query)) return true;
      // Search in destination
      if (trip.destination?.toLowerCase().includes(query)) return true;
      // Search in passenger name
      if (trip.trip_data?.passenger_name?.toLowerCase().includes(query)) return true;
      // Search in confirmation number
      if (trip.trip_data?.confirmation_number?.toLowerCase().includes(query)) return true;
      // Search in flights
      if (trip.trip_data?.flights?.some(f => 
        f.airline?.toLowerCase().includes(query) ||
        f.flight_number?.toLowerCase().includes(query) ||
        f.departure_city?.toLowerCase().includes(query) ||
        f.arrival_city?.toLowerCase().includes(query) ||
        f.departure_airport?.toLowerCase().includes(query) ||
        f.arrival_airport?.toLowerCase().includes(query)
      )) return true;
      // Search in hotels
      if (trip.trip_data?.hotels?.some(h => 
        h.hotel_name?.toLowerCase().includes(query) ||
        h.city?.toLowerCase().includes(query)
      )) return true;
      // Search in cars
      if (trip.trip_data?.cars?.some(c => 
        c.rental_company?.toLowerCase().includes(query) ||
        c.pickup_location?.toLowerCase().includes(query)
      )) return true;
      return false;
    });
  }, [trips, searchQuery]);

  // Split into active and completed trips, sorted by start date
  const { activeTrips, completedTrips } = useMemo(() => {
    const active: typeof filteredTrips = [];
    const completed: typeof filteredTrips = [];

    filteredTrips.forEach((trip) => {
      if (isTripCompleted(trip.end_date)) {
        completed.push(trip);
      } else {
        active.push(trip);
      }
    });

    // Sort active trips by start date (soonest first)
    active.sort((a, b) => {
      if (!a.start_date && !b.start_date) return 0;
      if (!a.start_date) return 1;
      if (!b.start_date) return -1;
      const dateA = safeParseDateString(a.start_date);
      const dateB = safeParseDateString(b.start_date);
      if (!dateA && !dateB) return 0;
      if (!dateA) return 1;
      if (!dateB) return -1;
      return dateA.getTime() - dateB.getTime();
    });

    // Sort completed trips by start date (most recent first)
    completed.sort((a, b) => {
      if (!a.start_date && !b.start_date) return 0;
      if (!a.start_date) return 1;
      if (!b.start_date) return -1;
      const dateA = safeParseDateString(a.start_date);
      const dateB = safeParseDateString(b.start_date);
      if (!dateA && !dateB) return 0;
      if (!dateA) return 1;
      if (!dateB) return -1;
      return dateB.getTime() - dateA.getTime();
    });

    return { activeTrips: active, completedTrips: completed };
  }, [filteredTrips]);

  if (authLoading || tripsLoading) {
    return (
      <div className="flex min-h-screen items-center justify-center">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-accent" />
      </div>
    );
  }

  const formatDateRange = (startDate: string | null, endDate: string | null) => {
    if (!startDate) return null;
    const start = safeFormat(startDate, "MMM d");
    if (start === "—") return null;
    if (!endDate) return start;
    const end = safeFormat(endDate, "MMM d, yyyy");
    if (end === "—") return start;
    return `${start} - ${end}`;
  };

  const getSourceBadge = (emailSource: string | null) => {
    if (emailSource === "email" || emailSource === "inbound") {
      return (
        <Badge variant="secondary" className="text-xs gap-1">
          <Mail className="h-3 w-3" />
          Email
        </Badge>
      );
    }
    return (
      <Badge variant="outline" className="text-xs gap-1">
        <Plane className="h-3 w-3" />
        Manual
      </Badge>
    );
  };

  const TripCard = ({ trip, isCompleted = false }: { trip: typeof trips[0]; isCompleted?: boolean }) => {
    const flightCount = trip.trip_data?.flights?.length || 0;
    const hotelCount = trip.trip_data?.hotels?.length || 0;
    const activityCount = trip.trip_data?.activities?.length || 0;

    return (
        <div className={`group bg-card rounded-xl border border-border p-4 sm:p-6 hover:shadow-elevated hover:border-accent/30 transition-all ${isCompleted ? 'opacity-75' : ''}`}>
        <div className="flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between">
          <Link to={`/trips/${trip.id}`} className="flex-1 min-w-0">
            <div className="flex items-center gap-2 mb-2">
              <h3 className="text-lg font-semibold text-foreground group-hover:text-accent transition-colors truncate">
                {trip.display_name}
              </h3>
              {isCompleted && (
                <CheckCircle className="h-4 w-4 text-green-500 flex-shrink-0" />
              )}
            </div>
            <div className="flex flex-wrap gap-4 text-sm text-muted-foreground mb-3">
              {trip.destination && (
                <div className="flex items-center gap-1">
                  <MapPin className="h-4 w-4" />
                  {trip.destination}
                </div>
              )}
              {trip.start_date && (
                <div className="flex items-center gap-1">
                  <Calendar className="h-4 w-4" />
                  {formatDateRange(trip.start_date, trip.end_date)}
                </div>
              )}
            </div>
            <div className="flex gap-6 text-sm">
              <div>
                <span className="text-muted-foreground">Flights:</span>
                <span className="ml-1 font-medium">{flightCount}</span>
              </div>
              <div>
                <span className="text-muted-foreground">Hotels:</span>
                <span className="ml-1 font-medium">{hotelCount}</span>
              </div>
              <div>
                <span className="text-muted-foreground">Activities:</span>
                <span className="ml-1 font-medium">{activityCount}</span>
              </div>
            </div>
          </Link>
          <div className="flex items-start gap-3 sm:ml-4">
            <div className="text-right">
              {getSourceBadge(trip.email_source)}
            </div>
            <div onClick={(e) => e.stopPropagation()} className="flex-shrink-0">
              <TripActionsMenu trip={trip} allTrips={trips || []} />
            </div>
          </div>
        </div>
      </div>
    );
  };

  const EmptyState = ({ message }: { message: string }) => (
    <div className="py-12 text-center text-muted-foreground">
      {message}
    </div>
  );

  return (
    <div className="flex min-h-screen flex-col bg-background">
      <Header />
      <main className="flex-1 max-w-7xl mx-auto px-4 sm:px-6 py-8 w-full">
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-6">
          <div>
            <h1 className="font-display text-3xl font-bold text-foreground">My Trips</h1>
            <p className="text-muted-foreground mt-1">
              {trips && trips.length > 0 
                ? `${trips.length} trip${trips.length !== 1 ? 's' : ''}`
                : 'Create your first trip or forward a booking email'
              }
            </p>
          </div>
          <div className="flex items-center gap-2">
            <ImportEmailDialog />
            <CreateTripDialog />
          </div>
        </div>

        {trips && trips.length > 0 ? (
          <>
            {/* Search Bar */}
            <div className="relative mb-8">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-muted-foreground" />
              <input
                type="text"
                placeholder="Search trips by name, destination, airline, hotel..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="w-full pl-10 pr-4 py-3 border border-border rounded-lg bg-card focus:outline-none focus:ring-2 focus:ring-ring focus:border-transparent"
              />
            </div>

            {/* Tabs for Active/Completed */}
            <Tabs defaultValue="active" className="w-full">
              <TabsList className="flex gap-4 mb-6 border-b border-border bg-transparent h-auto p-0">
                <TabsTrigger value="active" className="px-4 py-2 border-b-2 data-[state=active]:border-primary data-[state=active]:text-primary data-[state=inactive]:border-transparent data-[state=inactive]:text-muted-foreground hover:text-foreground bg-transparent rounded-none data-[state=active]:shadow-none">
                  Active Trips
                  <span className="ml-2 px-2 py-0.5 bg-primary/10 text-primary rounded-full text-xs">
                    {activeTrips.length}
                  </span>
                </TabsTrigger>
                <TabsTrigger value="completed" className="px-4 py-2 border-b-2 data-[state=active]:border-primary data-[state=active]:text-primary data-[state=inactive]:border-transparent data-[state=inactive]:text-muted-foreground hover:text-foreground bg-transparent rounded-none data-[state=active]:shadow-none">
                  Completed
                  {completedTrips.length > 0 && (
                    <span className="ml-2 px-2 py-0.5 bg-muted text-muted-foreground rounded-full text-xs">
                      {completedTrips.length}
                    </span>
                  )}
                </TabsTrigger>
              </TabsList>

              <TabsContent value="active">
                <div className="space-y-4">
                  {activeTrips.length > 0 ? (
                    activeTrips.map((trip) => (
                      <TripCard key={trip.id} trip={trip} />
                    ))
                  ) : (
                    <Card className="p-12">
                      <EmptyState
                        message={searchQuery ? "No active trips match your search" : "No upcoming trips"}
                      />
                    </Card>
                  )}
                </div>
              </TabsContent>

              <TabsContent value="completed">
                <div className="space-y-4">
                  {completedTrips.length > 0 ? (
                    completedTrips.map((trip) => (
                      <TripCard key={trip.id} trip={trip} isCompleted />
                    ))
                  ) : (
                    <Card className="p-12">
                      <EmptyState
                        message={searchQuery ? "No completed trips match your search" : "No completed trips yet"}
                      />
                    </Card>
                  )}
                </div>
              </TabsContent>
            </Tabs>
          </>
        ) : (
          <Card className="p-12">
            <div className="text-center">
              <div className="mx-auto w-16 h-16 rounded-full bg-accent/10 flex items-center justify-center mb-4">
                <Plane className="h-8 w-8 text-accent" />
              </div>
              <h2 className="font-display text-xl font-semibold text-foreground mb-2">
                No trips yet
              </h2>
              <p className="text-muted-foreground mb-6 max-w-sm mx-auto">
                Create a new trip manually or forward your booking confirmation emails to automatically import them.
              </p>
              <CreateTripDialog 
                trigger={
                  <button className="inline-flex items-center justify-center gap-2 rounded-lg bg-accent px-6 py-3 text-sm font-medium text-accent-foreground hover:bg-accent/90 transition-colors">
                    <Plus className="h-4 w-4" />
                    Create Your First Trip
                  </button>
                }
              />
            </div>
          </Card>
        )}
      </main>
      <Footer />
    </div>
  );
}
